import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserApiService } from '../../services/api/user-api.service';
import { UserDetail, GroupSummary, UpdateUserRequest } from '../../models/user.model';
import {
  PageHeaderComponent,
  ConfirmDialogComponent,
  DataGridComponent,
  GridToolbarComponent,
  GridColumn,
  ToolbarFilter,
  ToastService,
} from '../../shared/ui/public-api';

type ModalMode = 'create' | 'edit' | null;

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent, ConfirmDialogComponent, DataGridComponent, GridToolbarComponent],
  templateUrl: './users.html',
  styleUrls: ['./users.scss'],
})
export class UsersComponent implements OnInit {
  @ViewChild(DataGridComponent) grid!: DataGridComponent;

  groups: GroupSummary[] = [];
  gridFilters = '';

  readonly columns: GridColumn[] = [
    { field: 'id',         header: '#',          sortable: true  },
    { field: 'name',       header: 'Nome',        sortable: true  },
    { field: 'email',      header: 'E-mail',      sortable: true  },
    { field: 'verifiedAt', header: 'Verificado',  format: 'date', sortable: true },
    { field: 'createdAt',  header: 'Criado em',   format: 'date', sortable: true },
  ];

  readonly toolbarFilters: ToolbarFilter[] = [
    { field: 'name', placeholder: 'Buscar por nome ou e-mail…', op: 'contains' },
  ];

  // Modal
  modalMode: ModalMode = null;
  saving = false;
  modalError: string | null = null;
  createForm = { name: '', email: '', password: '' };
  editTarget: UserDetail | null = null;
  editForm: UpdateUserRequest = { name: '', groupIds: [] };

  // Confirmação
  actionTarget: UserDetail | null = null;
  actionType: 'deactivate' | 'reactivate' | null = null;

  get confirmMessage(): string {
    if (!this.actionTarget) return '';
    return this.actionType === 'deactivate'
      ? `Deseja desativar o usuário "${this.actionTarget.name}"?`
      : `Deseja reativar o usuário "${this.actionTarget.name}"?`;
  }

  constructor(private userApi: UserApiService, private toast: ToastService) {}

  ngOnInit(): void {
    this.userApi.getGroups().subscribe({ next: g => (this.groups = g), error: () => {} });
  }

  onFiltersChange(filters: string): void { this.gridFilters = filters; }

  onExport(type: 'csv' | 'xlsx' = 'csv'): void { this.grid?.exportData(type); }

  // ── Create ──────────────────────────────────────────────────
  openCreate(): void {
    this.modalMode = 'create';
    this.createForm = { name: '', email: '', password: '' };
    this.modalError = null;
  }

  submitCreate(): void {
    if (!this.createForm.name || !this.createForm.email || !this.createForm.password) {
      this.modalError = 'Preencha todos os campos obrigatórios.';
      return;
    }
    this.saving = true;
    this.modalError = null;
    this.userApi.registerUser(this.createForm).subscribe({
      next: () => {
        this.saving = false;
        this.modalMode = null;
        this.toast.show('Usuário criado com sucesso!', 'success');
        this.grid?.reload();
      },
      error: err => { this.saving = false; this.modalError = err?.error?.message ?? 'Erro ao criar usuário.'; },
    });
  }

  // ── Edit / Groups ────────────────────────────────────────────
  openEdit(row: unknown): void {
    const user = row as UserDetail;
    this.userApi.getUserDetail(user.id).subscribe({
      next: detail => {
        this.editTarget = detail;
        this.editForm = { name: detail.name, groupIds: detail.groups?.map(g => g.id) ?? [] };
        this.modalMode = 'edit';
        this.modalError = null;
      },
    });
  }

  isGroupSelected(groupId: number): boolean { return this.editForm.groupIds?.includes(groupId) ?? false; }

  toggleGroup(groupId: number): void {
    const ids = this.editForm.groupIds ?? [];
    this.editForm.groupIds = ids.includes(groupId) ? ids.filter(id => id !== groupId) : [...ids, groupId];
  }

  submitEdit(): void {
    if (!this.editTarget) return;
    this.saving = true;
    this.modalError = null;
    this.userApi.updateUser(this.editTarget.id, this.editForm).subscribe({
      next: () => {
        this.saving = false;
        this.modalMode = null;
        this.editTarget = null;
        this.toast.show('Usuário atualizado com sucesso!', 'success');
        this.grid?.reload();
      },
      error: err => { this.saving = false; this.modalError = err?.error?.message ?? 'Erro ao salvar usuário.'; },
    });
  }

  closeModal(): void { this.modalMode = null; this.editTarget = null; this.modalError = null; }

  // ── Deactivate / Reactivate ──────────────────────────────────
  confirmAction(row: unknown, type: 'deactivate' | 'reactivate'): void {
    this.actionTarget = row as UserDetail;
    this.actionType = type;
  }

  executeAction(): void {
    if (!this.actionTarget || !this.actionType) return;
    const id = this.actionTarget.id;
    const type = this.actionType;
    const obs = type === 'deactivate' ? this.userApi.deactivateUser(id) : this.userApi.reactivateUser(id);
    this.actionTarget = null;
    this.actionType = null;
    obs.subscribe({
      next: () => {
        const msg = type === 'deactivate'
          ? 'Usuário desativado com sucesso!'
          : 'Usuário reativado com sucesso!';
        this.toast.show(msg, 'success');
        this.grid?.reload();
      },
      error: err => alert(err?.error?.message ?? 'Erro ao executar ação.')
    });
  }

  cancelAction(): void { this.actionTarget = null; this.actionType = null; }
}
